"use client";

import { cn } from "@gettactic/helpers";
import { getIconColorForBackground } from "@gettactic/helpers/src/colors";
import { Check, Plus, Shuffle } from "lucide-react";
import React from "react";

/**
 * Preset pastel palette used by category cards and request chips. Values are hex so they
 * satisfy the backend's `hexcolor` validation on the category `color` field.
 */
export const CATEGORY_COLOR_PRESETS = [
  "#FEF3C7", // amber
  "#DCFCE7", // green
  "#E0E7FF", // indigo
  "#F3E8FF", // purple
  "#FEE2E2", // red
  "#F3F4F6" // neutral
] as const;

export const DEFAULT_CATEGORY_COLOR = CATEGORY_COLOR_PRESETS[0];

interface ColorSwatchSelectProps {
  value?: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  /**
   * Adds a leading "Random" swatch showing `color`. While it is selected the
   * caller keeps the color in sync with the category name, so the swatch owns
   * the selection instead of `value`.
   */
  random?: {
    color: string;
    isSelected: boolean;
    onSelect: () => void;
  };
}

export const ColorSwatchSelect: React.FC<ColorSwatchSelectProps> = ({
  value,
  onChange,
  disabled = false,
  random
}) => {
  // A selected random swatch owns the selection: the color it resolves to may
  // coincide with a preset, which must not light up as an explicit choice.
  const normalized = random?.isSelected ? undefined : value?.toUpperCase();
  const isPreset = CATEGORY_COLOR_PRESETS.some(
    (color) => color.toUpperCase() === normalized
  );
  const isCustom = !!normalized && !isPreset;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {random ? (
        <button
          type="button"
          disabled={disabled}
          aria-label="Use a random color based on the name"
          aria-pressed={random.isSelected}
          onClick={random.onSelect}
          style={{ backgroundColor: random.color }}
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-md border transition-shadow",
            random.isSelected
              ? "border-neutral-900 ring-2 ring-neutral-900/20"
              : "border-neutral-200 hover:border-neutral-400",
            disabled && "cursor-not-allowed opacity-60"
          )}
        >
          {/* The derived color is any hex, so the icon takes its contrast from it. */}
          <Shuffle
            className="h-4 w-4"
            style={{ color: getIconColorForBackground(random.color) }}
          />
        </button>
      ) : null}

      {CATEGORY_COLOR_PRESETS.map((color) => {
        const isSelected = normalized === color.toUpperCase();
        return (
          <button
            key={color}
            type="button"
            disabled={disabled}
            aria-label={`Select color ${color}`}
            aria-pressed={isSelected}
            onClick={() => onChange(color)}
            style={{ backgroundColor: color }}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-md border transition-shadow",
              isSelected
                ? "border-neutral-900 ring-2 ring-neutral-900/20"
                : "border-neutral-200 hover:border-neutral-400",
              disabled && "cursor-not-allowed opacity-60"
            )}
          >
            {isSelected ? <Check className="h-4 w-4 text-neutral-700" /> : null}
          </button>
        );
      })}

      {/* Custom color: a native color input rendered as a swatch. */}
      <label
        aria-label="Choose a custom color"
        style={isCustom ? { backgroundColor: value } : undefined}
        className={cn(
          "relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-md border transition-shadow",
          isCustom
            ? "border-neutral-900 ring-2 ring-neutral-900/20"
            : "border-neutral-200 hover:border-neutral-400",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        )}
      >
        {isCustom ? null : (
          <span
            aria-hidden
            className="absolute inset-0 bg-[conic-gradient(from_0deg,#f87171,#fbbf24,#34d399,#60a5fa,#a78bfa,#f87171)]"
          />
        )}
        {isCustom ? (
          <Check className="relative h-4 w-4 text-neutral-700" />
        ) : (
          <Plus className="relative h-4 w-4 text-white drop-shadow-sm" />
        )}
        <input
          type="color"
          disabled={disabled}
          value={isCustom && value ? value : "#7C3AED"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
      </label>
    </div>
  );
};
